import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { themaLesen, themaSetzen, type Thema } from "@mrmachine/ui";
import { useT } from "../i18n";
import { useSitzung } from "./Sitzung";
import { PfeilLinks } from "./Kopf";
import { Fassung } from "../bausteine/Fassung";
import { FASSUNG } from "../fassung";
import { Zeichen, type Zeichenname } from "../bausteine/Zeichen";
import { useMerkliste } from "../daten/merkliste";

/* ══════════════════════════════════════════════════════════════════
   Die Seitenleiste.

   **Seit „Signal" (07.10.2026) mit Zeichen vor dem Wort** — so wie im
   Muster (app-design-muster, Schreibtisch, Hülle). Bis dahin galt „keine
   Symbole", weil ein Piktogramm für „Vorgänge" erst gelernt werden müsste.
   Das Wort bleibt deshalb stehen; das Zeichen ist der Griff für die
   eingeklappte Leiste (56 px), in der nur noch es steht, mit dem Wort als
   Hinweis. Drei Gruppenüberschriften trennen, was man täglich anfasst, von
   dem, was man pflegt, und von dem, was Geld bewegt.

   **Jede Rolle bekommt eine eigene Liste.** Kein Punkt wird ausgegraut —
   was nicht erlaubt ist, erscheint nicht.

   Wer angemeldet ist und die Themawahl standen bis zum 07.10.2026 unten
   links; seit Signal stehen sie im Menü hinter dem Kürzel oben rechts
   (`Kopfwerkzeuge.tsx`). Unten steht die Fassung mit dem Rechteinhaber.

   **Die Leiste hat zwei Bahnen** (seit 16.09.2026): die Arbeit und die
   Einrichtung. Wer in die Einstellungen geht, sieht dieselbe Leiste
   hinüberschieben statt verschwinden und durch eine zweite ersetzt
   werden. Oben in der zweiten Bahn steht der Weg zurück zur Arbeit —
   dort, wo man ihn sucht. Was darunter steht, schreiben die
   Einstellungen selbst hinein (`Nebenleiste.tsx`).

   Der Fuß steht **unter** beiden Bahnen und wandert nicht mit.
   ══════════════════════════════════════════════════════════════════ */

/* **Wer welchen Punkt sieht, steht nicht mehr hier**, sondern in
   `schale/rechte.ts` — dieselbe Tabelle, die der Wegwächter im Router
   liest. Bis zum 16.09.2026 stand bei fast jedem Punkt `rollen: []`
   („jeder darf"), und der technische Außendienst bekam damit dasselbe
   Menü wie die Disposition: Brett, Stammdaten, Angebote, Preisstamm.
   Zwei Listen für dieselbe Frage hätten schon beim nächsten Punkt
   auseinandergelegen. */
type Punkt = { weg: string; text: string; zeichen: Zeichenname };
type Block = { gruppe?: string; trenn?: boolean; punkte?: Punkt[] };

const NAVIGATION: Block[] = [
  { gruppe: "nav.gruppeArbeit", punkte: [
    /* **Die Tagesmappe steht wieder in der Leiste** — aber nur für den, der
       sie braucht (16.09.2026). Der Punkt „Handy" war am Vortag zu Recht
       herausgeflogen: Er führte am Schreibtisch auf eine Maske für 390 px.
       Für den Außendienst ist das kein Umweg, sondern seine Arbeit; und wer
       am Telefon die Leiste öffnet, findet hier den Weg zurück zum Anfang. */
    /* **Ein Punkt statt zweier** (21.09.2026). Hier standen „Tagesmappe" und
       „Start" direkt untereinander — zwei Wege auf denselben Tag, und die
       Mappe am Schreibtisch eine 520-px-Spalte in einem 1440-px-Fenster.
       Luca: *„Vor allem auf dem Desktop klingt das auch irgendwie dumm, weil
       man da dann irgendwie Tagesmappe und Start hat. Das ist ja irgendwie
       Quatsch."* Der Tag steht jetzt auf demselben Schirm
       (`masken/Heute.tsx`), sobald die Rolle ihn darf. */
    { weg: "/heute", text: "nav.heute", zeichen: "sonne" },
    /* „Planung" stand hier bis zum 08.10.2026 — jetzt Kalender und Einteilen
       in der eigenen Gruppe „Disposition" darunter (docs/65). */
    { weg: "/vermieten", text: "nav.vermieten", zeichen: "vermieten" },
    { weg: "/angebote", text: "nav.angebote", zeichen: "angebot" },
    { weg: "/auftraege", text: "nav.auftraege", zeichen: "auftrag" },
    /* Die Ortsaufnahmen standen hier bis zum 08.10.2026 unter den Aufträgen,
       dann einen Tag gar nicht in der Leiste des Schreibtischs — jetzt in der
       Gruppe „Disposition" (Paket aufnahmen2, darunter). */
    /* **„Handy" stand hier und steht hier nicht mehr** (16.09.2026). Luca:
       *„Komisch, dieses Handy-Seitenmenü macht ja keinen Sinn. Das wollen
       wir nicht haben. Das ergibt sich ja daraus, wenn ich im Handy
       eingeloggt bin."*

       Ein Menüpunkt, der am Schreibtisch auf eine Maske für 390 px führt,
       ist ein Punkt, den man einmal anklickt und danach überliest. Der Weg
       dorthin kommt jetzt aus der Lage: Wer die Anwendung auf einem
       schmalen Gerät öffnet, landet darauf (`router.tsx`, die Wurzel).
       Zurück geht es am Telefon über „Mehr" (`Mehrblatt.tsx`). Der Weg
       heißt seit dem 19.09.2026 `/heute` und nicht mehr `/handy`
       (`schale/altadressen.ts`) — die Adresse nennt die Aufgabe, nicht das
       Gerät. */
  ] },
  /* **Disposition** (Luca, 08.10.2026, docs/65): Was wann verplant ist, und
     wer was macht — zwei Fragen, zwei Punkte. Vorher eine Seite „Planung"
     mit vier Schaltern. Einteilen liest und schreibt dasselbe Band wie der
     Kalender (Regel 8) — eine zweite Ansicht, keine zweite Liste. */
  { gruppe: "nav.gruppeDisposition", punkte: [
    { weg: "/kalender", text: "nav.kalender", zeichen: "planung" },
    { weg: "/einteilen", text: "nav.einteilen", zeichen: "aufgaben" },
    /* **Die Ortsaufnahmen sind zurück — in der Disposition** (Paket
       aufnahmen2, 08.10.2026). Am Vormittag waren sie aus der Leiste des
       Schreibtischs genommen worden; Luca danach: *„Jetzt habe ich aber
       nicht die Möglichkeit, die Ortsaufnahmen irgendwo wieder anzugucken."*
       Angelegt wird weiter im Fenster, verteilt in Einteilen; die Liste ist
       der Ort, an dem man sie wiederfindet — auch die gemessenen, aus denen
       später ein Angebot werden soll. Disposition, weil eine Ortsaufnahme
       Außendienst ist, den jemand einteilt. Am Telefon unter „Mehr" in
       derselben Gruppe. */
    { weg: "/aufnahmen", text: "nav.aufnahmen", zeichen: "ort" },
  ] },
  { gruppe: "nav.gruppeStammdaten", punkte: [
    { weg: "/kunden", text: "nav.kunden", zeichen: "kunden" },
    { weg: "/ressourcen", text: "nav.ressourcen", zeichen: "ressource" },
    { weg: "/preise", text: "nav.preise", zeichen: "preisschild" },
    { weg: "/weiteres", text: "nav.weiteres", zeichen: "weiteres" },
  ] },
  { gruppe: "nav.gruppeFinanzen", punkte: [
    { weg: "/abrechnung", text: "nav.abrechnung", zeichen: "abrechnung" },
    /* **Die Belegsuche** (18.09.2026): alle Angebote, Auftragsbestätigungen,
       Lieferscheine und Rechnungen, live aus Lexware Office. Unter den
       Finanzen, weil sie Belege zeigt — sehen darf sie aber auch, wer keine
       Abrechnung darf (`schale/rechte.ts`). Wer nur sie sieht, bekommt die
       Gruppe mit diesem einen Punkt. */
    { weg: "/belege", text: "nav.belege", zeichen: "belege" },
  ] },
  { trenn: true },
  { punkte: [
    { weg: "/einstellungen", text: "nav.einstellungen", zeichen: "zahnrad" },
  ] },
];

/**
 * Die Punkte der Seitenleiste, die jemand sieht — nach Gruppen, ohne Trenner.
 *
 * **Für „Mehr" am Telefon** (17.09.2026). Dort steht die untere Leiste über
 * jeder Maske, und was nicht in ihren Fächern liegt, muss ohne den
 * Menüknopf erreichbar sein. Die Liste kommt von hier und nicht aus einer
 * Abschrift: Ein Punkt, der in der Seitenleiste dazukommt, steht damit auch
 * unter „Mehr" — und eine Rolle, die ihn nicht darf, sieht ihn an beiden
 * Stellen nicht.
 */
export function seitenleisteFuer(
  darfAuf: (weg: string) => boolean,
): { gruppe?: string; punkte: Punkt[] }[] {
  return NAVIGATION
    .filter((b) => !b.trenn)
    .map((b) => ({
      ...(b.gruppe ? { gruppe: b.gruppe } : {}),
      punkte: (b.punkte ?? []).filter((p) => darfAuf(p.weg)),
    }))
    .filter((b) => b.punkte.length > 0);
}

/** Die drei Zustände der Darstellung: System · Hell · Dunkel. */
const THEMEN: { wert: Thema; text: string }[] = [
  { wert: "system", text: "thema.system" },
  { wert: "light", text: "thema.hell" },
  { wert: "dark", text: "thema.dunkel" },
];

/** Die Themawahl — unten in der Seitenleiste und am Telefon unter „Mehr" (`Mehrblatt.tsx`). */
export function ThemaLeiste() {
  const t = useT();
  const [thema, setzeThema] = useState<Thema>(() => themaLesen());

  const waehlen = (neu: Thema) => {
    themaSetzen(neu);
    setzeThema(neu);
  };

  return (
    <div className="thema-leiste" role="group" aria-label={t("thema.schild")}>
      {THEMEN.map((x) => (
        <button
          key={x.wert}
          type="button"
          aria-pressed={thema === x.wert}
          onClick={() => waehlen(x.wert)}
        >
          {t(x.text)}
        </button>
      ))}
    </div>
  );
}

export function Leiste({ offen, schliessen, einrichten, zielMerken }: {
  offen: boolean;
  schliessen: () => void;
  /** Steht die Leiste auf der zweiten Bahn — der Einrichtung? */
  einrichten: boolean;
  /** Wohin die Einstellungen ihre Navigation hängen (`Nebenleiste.tsx`). */
  zielMerken: (el: HTMLDivElement | null) => void;
}) {
  const t = useT();
  const { darfAuf } = useSitzung();
  const { location } = useRouterState();
  /* Wie viele Stücke in der Sammlung liegen — die Zahl am Punkt
     „Vermieten". Der Haken kostet keine Abfrage, er liest den Speicher
     der Seite (`daten/merkliste.ts`). */
  const { stuecke: gemerkt } = useMerkliste();

  return (
    <aside className="leiste" data-offen={offen ? "ja" : "nein"}>
      {/* Die Schiene schneidet ab, was neben der Leiste steht; die beiden
          Bahnen darin sind zusammen doppelt so breit und werden um eine
          halbe Breite verschoben. Die Bahn, die gerade nicht dran ist,
          ist `inert` — sonst tabbt man in eine Navigation, die man nicht
          sieht, und ein Vorleser liest beide. */}
      <div className="leiste-schiene">
        <div className="leiste-bahnen">
          <div className="leiste-bahn">
            <nav className="nav" aria-label={t("nav.hauptnavigation")} {...(einrichten ? { inert: true } : {})}>
              {NAVIGATION.map((block, i) => {
                if (block.trenn) return <div className="nav-trenn" key={`tr${i}`} />;
                const sichtbar = (block.punkte ?? []).filter((p) => darfAuf(p.weg));
                if (!sichtbar.length) return null;
                return (
                  <div key={block.gruppe ?? `b${i}`}>
                    {block.gruppe ? <div className="nav-gruppe">{t(block.gruppe)}</div> : null}
                    {sichtbar.map((p) => (
                      <Link
                        key={p.weg}
                        to={p.weg}
                        className="nav-punkt"
                        onClick={schliessen}
                        /* Eingeklappt steht nur das Zeichen — der Name dann als Hinweis. */
                        title={t(p.text)}
                        {...(location.pathname.startsWith(p.weg) ? { "aria-current": "page" as const } : {})}
                        {...(p.weg === "/vermieten" && gemerkt
                          ? { "aria-label": t("nav.vermietenMitSammlung", { zahl: gemerkt }) }
                          : {})}
                      >
                        <Zeichen name={p.zeichen} />
                        <span className="nav-wort">{t(p.text)}</span>
                        {/* **Die Sammlung sagt von außen, dass sie da ist**
                            (Luca, 17.09.2026). Seit sie in der Fläche eine
                            eigene Spalte hat, ist sie außerhalb davon
                            unsichtbar — und dann ist sie weg, ohne weg zu
                            sein. Die Zahl steht am Weg dorthin und
                            verschwindet mit dem letzten Posten.

                            **`nav-zahl` gibt es dafür schon** — aus dem
                            Prototyp, mit demselben Satz daneben: *„0 im
                            Postkorb steht nirgends, 2 im Postkorb schon"*
                            (`packages/ui/src/basis.css`). Bis heute hat
                            sie niemand benutzt; ein zweites Muster zu
                            erfinden wäre die teurere Variante davon. */}
                        {p.weg === "/vermieten" && gemerkt ? (
                          <span className="nav-zahl">{gemerkt}</span>
                        ) : null}
                      </Link>
                    ))}
                  </div>
                );
              })}
            </nav>
          </div>

          <div className="leiste-bahn">
            <nav className="nav" aria-label={t("einstellungen.titel")} {...(einrichten ? {} : { inert: true })}>
              {/* Der Weg zurück steht oben in der Leiste und nicht im
                  Maskenkopf: Wer einrichtet, geht nicht „eine Ebene
                  hoch", sondern aus der Einrichtung heraus — und das ist
                  dieselbe Bewegung wie das Hineingehen, nur rückwärts. */}
              <Link className="zurueck-arbeit" to="/heute" onClick={schliessen}>
                <PfeilLinks />
                <span>{t("kopf.zurueckZurArbeit")}</span>
              </Link>
              <div className="nav-trenn" />
              <div ref={zielMerken} />
            </nav>
          </div>
        </div>
      </div>

      {/* ── Der Fuß seit „Signal" (07.10.2026, docs/64, Paket 3) ──────────
          Wer angemeldet ist, und die Themawahl stehen jetzt im Menü hinter
          dem Kürzel oben rechts (`Kopfwerkzeuge.tsx`). Unten steht, welche
          Fassung läuft und wem sie gehört — Luca: *„unten an der
          Seitenleiste: Machine Office, einfach eine Versionierung,
          copyright …"*. Der Vermerk heißt genau „© Luca Sliwinski"
          (klein, mit Punkt — Luca, 07.10.2026). In den Einstellungen kommen
          Zweig und Stand dazu, wie bisher: Wer einen Fehler meldet, schaut
          dort. */}
      <div className="leiste-fuss">
        <span className="leiste-fuss-marke">
          {t("leiste.fussMarke")} {FASSUNG.version ?? t("app.fassungUnbekannt")}
        </span>
        <span>{t("leiste.fussRecht")}</span>
        {einrichten ? <span className="leiste-fassung"><Fassung stand /></span> : null}
      </div>
    </aside>
  );
}
